/**
 * HomePage Component
 * 
 * 메인 홈페이지 - 로그인/비로그인 상태에 따라 다른 화면 표시
 * 반응형: 모바일 / 태블릿 / 데스크탑
 */

import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../auth';
import { useLanguage, parseUTCDate } from '../i18n';
import { getEvaluationSummary, getHistory, getQuotaInfo, getCurrentNews, APP_ENV } from '../api';
import type { QuotaInfo, SoftwareNewsItem } from '../api';
import { analytics } from '../analytics';
import { useBodyScrollLock } from '../hooks';

// 함께한 날수 계산 함수
function getDaysTogether(createdAt: string): number {
  const created = parseUTCDate(createdAt);
  const now = new Date();
  const diffTime = now.getTime() - created.getTime();
  return Math.max(1, Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1);
}

// 배지 정의
interface Badge {
  id: string;
  emoji: string;
  nameKo: string;
  nameEn: string;
  descriptionKo: string;
  descriptionEn: string;
  condition: (days: number, evaluations: number) => boolean;
}

const BADGES: Badge[] = [
  // 가입 기념
  { 
    id: 'newbie', 
    emoji: '🌱', 
    nameKo: '새싹', 
    nameEn: 'Newbie',
    descriptionKo: '서비스에 가입하셨습니다!',
    descriptionEn: 'You joined the service!',
    condition: (days) => days >= 1,
  },
  // 평가 횟수 기반
  { 
    id: 'explorer', 
    emoji: '🔍', 
    nameKo: '탐험가', 
    nameEn: 'Explorer',
    descriptionKo: '3회 이상 평가를 완료했습니다.',
    descriptionEn: 'Completed 3+ evaluations.',
    condition: (_, evaluations) => evaluations >= 3,
  },
  { 
    id: 'regular', 
    emoji: '⭐', 
    nameKo: '단골', 
    nameEn: 'Regular',
    descriptionKo: '10회 이상 평가를 완료했습니다.',
    descriptionEn: 'Completed 10+ evaluations.',
    condition: (_, evaluations) => evaluations >= 10,
  },
  { 
    id: 'veteran', 
    emoji: '🏅', 
    nameKo: '베테랑', 
    nameEn: 'Veteran',
    descriptionKo: '30일 이상 함께하며 20회 이상 평가했습니다.',
    descriptionEn: '30+ days with 20+ evaluations.',
    condition: (days, evaluations) => days >= 30 && evaluations >= 20,
  },
  { 
    id: 'legend', 
    emoji: '🏆', 
    nameKo: '레전드', 
    nameEn: 'Legend',
    descriptionKo: '100일 이상 함께하며 50회 이상 평가했습니다.',
    descriptionEn: '100+ days with 50+ evaluations.',
    condition: (days, evaluations) => days >= 100 && evaluations >= 50,
  },
  // 기간 마일스톤 (150일~1년)
  { 
    id: 'day150', 
    emoji: '🌟', 
    nameKo: '150일', 
    nameEn: '150 Days',
    descriptionKo: '150일 동안 함께했습니다!',
    descriptionEn: '150 days together!',
    condition: (days) => days >= 150,
  },
  { 
    id: 'day200', 
    emoji: '💫', 
    nameKo: '200일', 
    nameEn: '200 Days',
    descriptionKo: '200일 동안 함께했습니다!',
    descriptionEn: '200 days together!',
    condition: (days) => days >= 200,
  },
  { 
    id: 'day250', 
    emoji: '✨', 
    nameKo: '250일', 
    nameEn: '250 Days',
    descriptionKo: '250일 동안 함께했습니다!',
    descriptionEn: '250 days together!',
    condition: (days) => days >= 250,
  },
  { 
    id: 'day300', 
    emoji: '🎯', 
    nameKo: '300일', 
    nameEn: '300 Days',
    descriptionKo: '300일 동안 함께했습니다!',
    descriptionEn: '300 days together!',
    condition: (days) => days >= 300,
  },
  { 
    id: 'day350', 
    emoji: '🎪', 
    nameKo: '350일', 
    nameEn: '350 Days',
    descriptionKo: '350일 동안 함께했습니다!',
    descriptionEn: '350 days together!',
    condition: (days) => days >= 350,
  },
  { 
    id: 'day365', 
    emoji: '🎂', 
    nameKo: '1주년', 
    nameEn: '1 Year',
    descriptionKo: '1년 동안 함께했습니다! 축하해요! 🎉',
    descriptionEn: '1 year together! Congratulations! 🎉',
    condition: (days) => days >= 365,
  },
];

// 배지 결정 함수
function getBadgesWithStatus(days: number, totalEvaluations: number) {
  return BADGES.map((badge) => ({
    ...badge,
    unlocked: badge.condition(days, totalEvaluations),
  }));
}

interface HomePageProps {
  onStartEvaluation: () => void;
  onOpenPricing: () => void;
  onOpenHistory: () => void;
  onOpenHistoryItem?: (recordId: number) => void;
  onOpenDashboard: () => void;
  onOpenStatistics: () => void;
  onOpenTestcaseAnalytics: () => void;
  onOpenCriteriaSettings: () => void;
  onOpenTeamDashboard: () => void;
  onOpenTestcaseManager: () => void;
  onDemoSubmit: () => void;
  onOpenProfile?: () => void;
}

export default function HomePage({
  onStartEvaluation,
  onOpenPricing,
  onOpenHistory,
  onOpenHistoryItem,
  onOpenDashboard: _onOpenDashboard,
  onOpenStatistics,
  onOpenTestcaseAnalytics,
  onOpenCriteriaSettings,
  onOpenTeamDashboard,
  onOpenTestcaseManager,
  onDemoSubmit,
  onOpenProfile,
}: HomePageProps) {
  const { t, lang } = useLanguage();
  const { isAuthenticated, user, quota } = useAuth();
  const [recentHistory, setRecentHistory] = useState<Array<{ id: number; feature_name: string; status: string | null; created_at: string }>>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [statsTotal, setStatsTotal] = useState(0);
  const [thisMonthTotal, setThisMonthTotal] = useState(0);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [localQuota, setLocalQuota] = useState<QuotaInfo | null>(null);
  const [currentNews, setCurrentNews] = useState<SoftwareNewsItem | null>(null);
  const [isLoadingNews, setIsLoadingNews] = useState(false);
  
  // 모달 상태
  const [showDaysModal, setShowDaysModal] = useState(false);
  const [showBadgesModal, setShowBadgesModal] = useState(false);
  const [showQuotaExceededToast, setShowQuotaExceededToast] = useState(false);

  // 함께한 날/배지 팝업이 열려 있을 때 배경 스크롤 방지
  useBodyScrollLock(!!isAuthenticated && (showDaysModal || showBadgesModal));

  // 최근 히스토리 로드
  useEffect(() => {
    if (isAuthenticated && quota?.limits.history_limit && quota.limits.history_limit > 0) {
      setIsLoadingHistory(true);

      // 히스토리 카드: 개인+팀 합산 (evaluation summary 기준)
      // 통계 카드: 내가 접근 가능한 전체 평가(개인+팀 포함) 요약
      Promise.all([
        getHistory(1, 5), // 최근 히스토리 (기본 team_id=None)
        getEvaluationSummary(),
      ])
        .then(([personal, summary]) => {
          setRecentHistory(personal.items.slice(0, 3));
          // 히스토리 카드에도 개인+팀 합산 표시
          setHistoryTotal(summary.total);
          setStatsTotal(summary.total);
          setThisMonthTotal(summary.this_month);
        })
        .catch(console.error)
        .finally(() => setIsLoadingHistory(false));
    }
  }, [isAuthenticated, quota]);

  // Quota 정보
  useEffect(() => {
    if (isAuthenticated) {
      getQuotaInfo().then(setLocalQuota).catch(console.error);
    }
  }, [isAuthenticated]);

  // 뉴스 로드
  useEffect(() => {
    setIsLoadingNews(true);
    getCurrentNews(lang)
      .then(setCurrentNews)
      .catch(console.error)
      .finally(() => setIsLoadingNews(false));
  }, [lang]);

  // AuthContext의 quota를 우선 사용 (DevPlanSwitcher 오버라이드 반영)
  const effectiveQuota = quota || localQuota;

  // Quota 초과 여부 계산
  const isQuotaExceeded = useMemo(() => {
    if (!effectiveQuota) return false;
    if (effectiveQuota.monthly_evaluations.unlimited) return false;
    return effectiveQuota.monthly_evaluations.used >= effectiveQuota.monthly_evaluations.limit;
  }, [effectiveQuota]);

  // 함께한 날
  const daysTogether = useMemo(() => {
    if (!user?.created_at) return 1;
    return getDaysTogether(user.created_at);
  }, [user?.created_at]);

  // 배지
  const badges = useMemo(() => {
    return getBadgesWithStatus(daysTogether, historyTotal);
  }, [daysTogether, historyTotal]);

  const unlockedBadgeCount = badges.filter(b => b.unlocked).length;

  // 비로그인 랜딩
  if (!isAuthenticated) {
    return <GuestLanding onTryDemo={onDemoSubmit} onSignUp={onOpenPricing} />;
  }

  // 로그인 홈
  return (
    <div className="space-y-4 md:space-y-6" data-testid="home-page">
      {/* 상단: 인사말 + 릴리즈 평가 버튼 (2:1 비율) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* 인사말 섹션 (프로필 연결) - 2칸 */}
        <section 
          className="md:col-span-2 neo-card-static bg-gradient-to-br from-[var(--neo-lime)] to-[var(--neo-mint)] dark:from-[#1e2a3a] dark:to-[#0f1a2a] p-4 md:p-5 cursor-pointer hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0_var(--neo-shadow)] transition-all" 
          data-testid="home-greeting-section"
          onClick={onOpenProfile}
        >
          <div className="flex items-center gap-3 h-full">
            {/* 프로필 아바타 */}
            <div className="w-12 h-12 md:w-14 md:h-14 bg-white dark:bg-[var(--neo-mint)] rounded-full flex items-center justify-center text-2xl border-3 border-black shadow-[3px_3px_0_var(--neo-shadow)] flex-shrink-0">
              {user?.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name || ''} 
                  className="w-full h-full rounded-full object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <span>👤</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-base md:text-lg font-black text-black dark:text-white truncate" data-testid="home-greeting">
                {lang === 'ko' ? `안녕하세요, ${user?.name || '사용자'}님! 👋` : `Hello, ${user?.name || 'User'}! 👋`}
              </h2>
              <p className="text-xs text-black/60 dark:text-white/60 flex items-center gap-2">
                <span>{lang === 'ko' ? '프로필 보기' : 'View Profile'}</span>
                <span>→</span>
              </p>
            </div>
          </div>
        </section>

        {/* 릴리즈 평가 버튼 - 1칸 */}
        <button
          onClick={() => {
            if (isQuotaExceeded) {
              setShowQuotaExceededToast(true);
              return;
            }
            analytics.formStart();
            onStartEvaluation();
          }}
          className={`neo-btn px-4 py-4 md:py-0 text-base md:text-lg font-black flex flex-col items-center justify-center gap-1 h-full transition-all ${
            isQuotaExceeded
              ? 'bg-terminal-muted/50 dark:bg-terminal-muted/30 text-terminal-dim dark:text-terminal-dim cursor-not-allowed opacity-70'
              : 'bg-black text-[var(--neo-lime)] dark:bg-[var(--neo-mint)] dark:text-black'
          }`}
          data-testid="home-start-evaluation-btn"
        >
          <div className="flex items-center gap-2">
            <span className="text-xl">{isQuotaExceeded ? '🔒' : '🚀'}</span>
            <span className="whitespace-nowrap">{t.home?.startEvaluation || '릴리즈 평가'}</span>
          </div>
          {/* Quota 초과 안내 - 버튼 내부 */}
          {isQuotaExceeded && (
            <span className="text-[10px] text-terminal-muted">
              {lang === 'ko' 
                ? '🔓 플랜 업그레이드로 더 많은 평가' 
                : '🔓 Upgrade for more'}
            </span>
          )}
        </button>
      </div>

      {/* Quota 초과 모달 */}
      {showQuotaExceededToast && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setShowQuotaExceededToast(false)}
        >
          <div 
            className="bg-[#1a1a2e] border-2 border-terminal-border rounded-lg p-6 shadow-2xl max-w-sm mx-4 animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-4">
              <span className="text-5xl">🔒</span>
            </div>
            <h3 className="text-lg text-terminal-text font-bold text-center mb-2">
              {lang === 'ko' 
                ? '월간 평가 횟수 초과' 
                : 'Monthly Limit Reached'}
            </h3>
            <p className="text-sm text-terminal-muted text-center mb-5">
              {lang === 'ko' 
                ? '이번 달 평가 횟수를 모두 사용했습니다.\n더 많은 평가가 필요하시면 플랜을 업그레이드하세요.' 
                : 'You have used all your evaluations this month.\nUpgrade your plan for more evaluations.'}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowQuotaExceededToast(false)}
                className="flex-1 px-4 py-2.5 text-sm border border-terminal-border text-terminal-muted rounded-lg hover:text-terminal-text hover:border-terminal-muted transition-colors"
              >
                {lang === 'ko' ? '닫기' : 'Close'}
              </button>
              <button
                onClick={() => {
                  setShowQuotaExceededToast(false);
                  onOpenPricing();
                }}
                className="flex-1 px-4 py-2.5 text-sm bg-[var(--neo-mint)]/20 border border-[var(--neo-mint)] text-[var(--neo-mint)] rounded-lg hover:bg-[var(--neo-mint)]/30 transition-colors font-medium"
              >
                {lang === 'ko' ? '플랜 업그레이드' : 'Upgrade Plan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 대시보드 그리드 - 모바일: 1열 / 태블릿: 2열 / 데스크탑: 3열 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="home-dashboard-grid">
        {/* 현재 플랜 카드 (요금제 보기 대체) */}
        <DashboardCard
          testId="home-plan-card"
          icon="📊"
          title={t.dashboard.currentPlan}
          onClick={onOpenPricing}
        >
          <div className="flex items-center gap-3 mb-3">
            <span className={`neo-badge text-xs px-2 py-0.5 ${
              effectiveQuota?.plan === 'team' 
                ? 'bg-[var(--neo-yellow)] text-black' 
                : effectiveQuota?.plan === 'pro' 
                  ? 'bg-[var(--neo-mint)] text-black' 
                  : effectiveQuota?.plan === 'max'
                    ? 'bg-[var(--neo-orange)] text-black'
                    : 'bg-white text-black'
            }`}>
              {effectiveQuota?.plan?.toUpperCase() || 'FREE'}
            </span>
          </div>
          {effectiveQuota?.monthly_evaluations.unlimited ? (
            <p className="text-sm text-terminal-text">∞ {t.dashboard.unlimited}</p>
          ) : (() => {
            const used = effectiveQuota?.monthly_evaluations.used || 0;
            const limit = effectiveQuota?.monthly_evaluations.limit || 3;
            const isExceeded = used > limit;
            return (
              <div>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-terminal-muted">{t.dashboard.monthlyEvaluations}</span>
                  <span className={`font-bold ${isExceeded ? 'text-status-danger' : 'text-terminal-text'}`}>
                    {used} / {limit}
                  </span>
                </div>
                <div className="h-1.5 bg-black/10 dark:bg-white/10 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${isExceeded ? 'bg-status-danger' : 'bg-[var(--neo-mint)]'}`}
                    style={{ width: `${Math.min((used / limit) * 100, 100)}%` }}
                  />
                </div>
              </div>
            );
          })()}
        </DashboardCard>

        {/* 함께한 날 카드 (클릭 시 모달) */}
        <DashboardCard
          testId="home-days-card"
          icon="📅"
          title={t.home?.daysTogether || '함께한 날'}
          onClick={() => setShowDaysModal(true)}
        >
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-black text-status-go tabular-nums" data-testid="home-days-count">
              D+{daysTogether.toLocaleString()}
            </span>
          </div>
          <p className="text-xs text-terminal-muted mt-1">
            {lang === 'ko' ? '자세히 보기 →' : 'View details →'}
          </p>
        </DashboardCard>

        {/* 배지 카드 (클릭 시 모달) */}
        <DashboardCard
          testId="home-badges-card"
          icon="🎖️"
          title={t.home?.badges || '획득한 배지'}
          onClick={() => setShowBadgesModal(true)}
        >
          <div className="flex flex-wrap gap-2 mb-2">
            {badges.slice(0, 5).map((badge) => (
              <span
                key={badge.id}
                className={`text-2xl transition-all ${
                  badge.unlocked 
                    ? 'opacity-100 scale-100' 
                    : 'opacity-20 grayscale scale-90'
                }`}
                data-testid={`badge-${badge.id}`}
              >
                {badge.emoji}
              </span>
            ))}
          </div>
          <p className="text-xs text-terminal-muted">
            {unlockedBadgeCount}/{badges.length} {lang === 'ko' ? '획득 · 자세히 보기 →' : 'earned · View details →'}
          </p>
        </DashboardCard>

        {/* 히스토리 카드 (추가됨) */}
        <DashboardCard
          testId="home-history-card"
          icon="📚"
          title={t.history.viewHistory}
          onClick={onOpenHistory}
        >
          <div className="flex items-baseline gap-1 mb-2">
            <span className="text-2xl font-black text-terminal-text tabular-nums">
              {historyTotal}
            </span>
            <span className="text-xs text-terminal-muted">{lang === 'ko' ? '건' : 'records'}</span>
          </div>
          <p className="text-xs text-terminal-muted">
            {lang === 'ko' ? '평가 기록 보기 →' : 'View evaluation history →'}
          </p>
        </DashboardCard>

        {/* 평가 통계 카드 */}
        {effectiveQuota?.limits.history_limit && effectiveQuota.limits.history_limit > 0 && (
          <DashboardCard
            testId="home-stats-card"
            icon="📈"
            title={t.statistics?.title || '평가 통계'}
            onClick={onOpenStatistics}
          >
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-2xl font-black text-terminal-text" data-testid="home-total-evaluations">
                  {statsTotal}
                </p>
                <p className="text-xs text-terminal-muted">{t.home?.totalEvaluations || '총 평가'}</p>
              </div>
              <div>
                <p className="text-2xl font-black text-terminal-text" data-testid="home-this-month">
                  {thisMonthTotal}
                </p>
                <p className="text-xs text-terminal-muted">{t.home?.thisMonth || '이번 달'}</p>
              </div>
            </div>
          </DashboardCard>
        )}

        {/* 테스트케이스 분석 카드 */}
        {effectiveQuota?.limits.history_limit && effectiveQuota.limits.history_limit > 0 && (
          <DashboardCard
            testId="home-testcase-analytics-card"
            icon="🔬"
            title={lang === 'ko' ? '테스트케이스 분석' : 'Test Case Analytics'}
            badge="NEW"
            onClick={onOpenTestcaseAnalytics}
          >
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-2xl font-black text-terminal-text">
                  {historyTotal > 0 ? '📊' : '—'}
                </p>
                <p className="text-xs text-terminal-muted">{lang === 'ko' ? '취약 영역' : 'Weak Areas'}</p>
              </div>
              <div>
                <p className="text-2xl font-black text-terminal-text">
                  {historyTotal > 0 ? '📈' : '—'}
                </p>
                <p className="text-xs text-terminal-muted">{lang === 'ko' ? '트렌드' : 'Trends'}</p>
              </div>
            </div>
          </DashboardCard>
        )}

        {/* 릴리즈 기준 설정 카드 (Max/Team) */}
        {(effectiveQuota?.plan === 'max' || effectiveQuota?.plan === 'team') && (
          <DashboardCard
            testId="home-criteria-card"
            icon="⚙️"
            title={t.criteria?.title || '릴리즈 판정 기준'}
            badge="MAX"
            onClick={onOpenCriteriaSettings}
          >
            <p className="text-xs text-terminal-muted">
              {lang === 'ko' ? 'Go/No-Go 기준 커스터마이즈' : 'Customize Go/No-Go criteria'}
            </p>
          </DashboardCard>
        )}

        {/* 팀 대시보드 카드 (Team) */}
        {effectiveQuota?.plan === 'team' && effectiveQuota.limits.team_dashboard && (
          <DashboardCard
            testId="home-team-card"
            icon="👥"
            title={t.dashboard.teamDashboardTitle || '팀 대시보드'}
            badge="TEAM"
            onClick={onOpenTeamDashboard}
          >
            <p className="text-xs text-terminal-muted">
              {lang === 'ko' ? '팀 전체 릴리즈 품질 현황' : 'Team release quality overview'}
            </p>
          </DashboardCard>
        )}

        {/* 테스트케이스 관리 카드 (local/staging 환경에서만 표시) */}
        {(APP_ENV === 'local' || APP_ENV === 'staging') && (
          <DashboardCard
            testId="home-testcase-manager-card"
            icon="📋"
            title={t.testcaseManager?.title || '테스트케이스 관리'}
            onClick={onOpenTestcaseManager}
          >
            <p className="text-xs text-terminal-muted">
              {lang === 'ko' ? '테스트케이스 생성 및 관리 →' : 'Create and manage test cases →'}
            </p>
          </DashboardCard>
        )}
      </div>

      {/* 최근 활동 */}
      <section className="neo-card-static bg-white dark:bg-[#1e2a3a] p-4 md:p-6" data-testid="home-recent-activity">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-black text-black dark:text-white uppercase flex items-center gap-2">
            <span className="w-2 h-2 bg-[var(--neo-mint)]"></span>
            {t.home?.recentActivity || '최근 활동'}
          </h3>
          {historyTotal > 0 && (
            <button
              onClick={onOpenHistory}
              className="text-xs text-terminal-muted hover:text-terminal-text transition-colors"
              data-testid="home-view-all-history-btn"
            >
              {t.home?.viewAllHistory || '전체 보기'} →
            </button>
          )}
        </div>

        {isLoadingHistory ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 bg-black/5 dark:bg-white/5 animate-pulse rounded" />
            ))}
          </div>
        ) : recentHistory.length > 0 ? (
          <div className="space-y-2">
            {recentHistory.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between p-3 bg-black/5 dark:bg-white/5 rounded-lg hover:bg-black/10 dark:hover:bg-white/10 transition-colors cursor-pointer"
                onClick={() => (onOpenHistoryItem ? onOpenHistoryItem(item.id) : onOpenHistory())}
                data-testid={`recent-item-${item.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <StatusBadge status={item.status} />
                  <span className="text-sm text-terminal-text truncate">{item.feature_name}</span>
                </div>
                <span className="text-xs text-terminal-muted whitespace-nowrap">
                  {parseUTCDate(item.created_at).toLocaleDateString(lang === 'ko' ? 'ko-KR' : 'en-US', {
                    month: 'short',
                    day: 'numeric',
                  })}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-terminal-muted text-sm mb-4">{t.home?.noRecentActivity || '아직 평가 기록이 없어요'}</p>
            <button
              onClick={onStartEvaluation}
              className="neo-btn bg-[var(--neo-mint)] text-black px-4 py-2 text-sm"
              data-testid="home-first-evaluation-btn"
            >
              {lang === 'ko' ? '첫 평가 시작하기' : 'Start First Evaluation'}
            </button>
          </div>
        )}
      </section>

      {/* 소프트웨어 뉴스 섹션 */}
      <section className="neo-card-static bg-gradient-to-br from-[var(--neo-blue)] to-[var(--neo-mint)] dark:from-[#0f1a2a] dark:to-[#1e2a3a] p-4 md:p-6" data-testid="home-news-section">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-lg">{currentNews?.time_slot === 'night' ? '🌙' : '📰'}</span>
          <h3 className="text-sm font-black text-black dark:text-white uppercase">
            {currentNews?.time_slot === 'night' 
              ? (lang === 'ko' ? '하루 정리 브리핑' : 'Daily Briefing')
              : (lang === 'ko' ? '소프트웨어 뉴스' : 'Software News')
            }
          </h3>
          <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded text-white ${
            currentNews?.time_slot === 'night' 
              ? 'bg-gradient-to-r from-purple-600 to-indigo-600' 
              : 'bg-gradient-to-r from-purple-500 to-cyan-500'
          }`}>
            {currentNews?.time_slot === 'night' 
              ? (lang === 'ko' ? '21시' : '9PM')
              : (lang === 'ko' ? '오늘' : 'TODAY')
            }
          </span>
        </div>

        {isLoadingNews ? (
          <div className="h-20 bg-black/5 dark:bg-white/5 animate-pulse rounded" />
        ) : currentNews ? (
          <div className="space-y-2">
            <h4 className="text-base font-bold text-black dark:text-white leading-snug">
              {currentNews.title}
            </h4>
            <p className="text-sm text-black/70 dark:text-white/70 line-clamp-2">
              {currentNews.summary}
            </p>
            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-2 text-xs text-black/50 dark:text-white/50">
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                  currentNews.time_slot === 'morning' ? 'bg-orange-200 text-orange-800' :
                  currentNews.time_slot === 'noon' ? 'bg-yellow-200 text-yellow-800' :
                  currentNews.time_slot === 'evening' ? 'bg-indigo-200 text-indigo-800' :
                  'bg-purple-200 text-purple-800'
                }`}>
                  {currentNews.time_slot === 'morning' ? '🌅 06시' : 
                   currentNews.time_slot === 'noon' ? '☀️ 12시' : 
                   currentNews.time_slot === 'evening' ? '🌆 18시' : '🌙 21시'}
                </span>
                {currentNews.source_name && <span>{currentNews.source_name}</span>}
              </div>
              {currentNews.source_url && (
                <a
                  href={currentNews.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-terminal-text hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {lang === 'ko' ? '원문 보기 →' : 'Read more →'}
                </a>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-sm text-black/50 dark:text-white/50">
              {lang === 'ko' ? '오늘의 뉴스가 곧 업데이트됩니다' : 'Today\'s news coming soon'}
            </p>
          </div>
        )}
      </section>

      {/* 함께한 날 상세 모달 */}
      {showDaysModal && (
        <DaysTogetherModal
          days={daysTogether}
          createdAt={user?.created_at || ''}
          onClose={() => setShowDaysModal(false)}
        />
      )}

      {/* 배지 상세 모달 */}
      {showBadgesModal && (
        <BadgesModal
          badges={badges}
          onClose={() => setShowBadgesModal(false)}
        />
      )}
    </div>
  );
}

// 대시보드 카드 컴포넌트
function DashboardCard({
  testId,
  icon,
  title,
  badge,
  onClick,
  children,
}: {
  testId: string;
  icon: string;
  title: string;
  badge?: string;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  const Component = onClick ? 'button' : 'div';
  
  return (
    <Component
      className={`neo-card-static bg-white dark:bg-[#1e2a3a] p-4 text-left w-full h-[150px] overflow-hidden flex flex-col ${
        onClick ? 'hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0_var(--neo-shadow)] transition-all cursor-pointer' : ''
      }`}
      onClick={onClick}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">{icon}</span>
          <h4 className="text-xs font-bold text-black/60 dark:text-white/60 uppercase">{title}</h4>
        </div>
        {badge && (
          <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded ${
            badge === 'NEW' 
              ? 'bg-gradient-to-r from-purple-500 to-cyan-500 text-white'
              : badge === 'MAX'
                ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white'
                : badge === 'TEAM'
                  ? 'bg-[var(--neo-yellow)] text-black'
                  : 'bg-terminal-muted text-white'
          }`}>
            {badge}
          </span>
        )}
      </div>
      <div className="flex-1 min-h-0 flex flex-col justify-end">
        {children}
      </div>
    </Component>
  );
}

// 상태 배지 컴포넌트
function StatusBadge({ status }: { status: string | null }) {
  const config: Record<string, { bg: string; text: string }> = {
    go: { bg: 'bg-status-go', text: 'GO' },
    conditional: { bg: 'bg-status-warning', text: 'COND' },
    holding: { bg: 'bg-status-holding', text: 'HOLD' },
    no_go: { bg: 'bg-status-danger', text: 'NO' },
  };
  const { bg, text } = config[status || 'no_go'] || config.no_go;
  
  return (
    <span className={`${bg} text-white text-[10px] font-bold px-1.5 py-0.5 rounded`}>
      {text}
    </span>
  );
}

// 함께한 날 상세 모달
function DaysTogetherModal({
  days,
  createdAt,
  onClose,
}: {
  days: number;
  createdAt: string;
  onClose: () => void;
}) {
  const { lang } = useLanguage();
  const joinDate = parseUTCDate(createdAt);
  
  // 다음 마일스톤 계산
  const milestones = [7, 30, 100, 365, 1000];
  const nextMilestone = milestones.find(m => m > days) || days;
  const daysUntilNext = nextMilestone - days;
  const progressPercent = Math.min((days / nextMilestone) * 100, 100);
  
  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="neo-card-static bg-white dark:bg-[#1e2a3a] p-6 max-w-sm w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-black text-black dark:text-white">
            📅 {lang === 'ko' ? '함께한 날' : 'Days Together'}
          </h3>
          <button
            onClick={onClose}
            className="text-2xl text-terminal-muted hover:text-terminal-text"
          >
            ×
          </button>
        </div>

        <div className="text-center mb-6">
          <div className="text-5xl font-black text-status-go mb-2">
            D+{days.toLocaleString()}
          </div>
          <p className="text-sm text-terminal-muted">
            {lang === 'ko' 
              ? `${joinDate.getFullYear()}년 ${joinDate.getMonth() + 1}월 ${joinDate.getDate()}일 가입`
              : `Joined ${joinDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`
            }
          </p>
        </div>

        {daysUntilNext > 0 && (
          <div className="bg-black/5 dark:bg-white/5 rounded-lg p-4">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-terminal-muted">
                {lang === 'ko' ? `다음 마일스톤: D+${nextMilestone}` : `Next milestone: D+${nextMilestone}`}
              </span>
              <span className="text-terminal-text font-bold">
                {lang === 'ko' ? `${daysUntilNext}일 남음` : `${daysUntilNext} days left`}
              </span>
            </div>
            <p className="text-[10px] text-terminal-muted/70 mb-2">
              {lang === 'ko' 
                ? '🎯 마일스톤은 서비스와 함께한 기념일이에요 (D+7, 30, 100, 365, 1000)' 
                : '🎯 Milestones celebrate your journey with us (D+7, 30, 100, 365, 1000)'}
            </p>
            <div className="h-2 bg-black/10 dark:bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[var(--neo-lime)] to-[var(--neo-mint)] transition-all"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        )}

        <button
          onClick={onClose}
          className="neo-btn w-full bg-black text-white dark:bg-[var(--neo-mint)] dark:text-black mt-6"
        >
          {lang === 'ko' ? '닫기' : 'Close'}
        </button>
      </div>
    </div>
  );
}

// 배지 상세 모달
function BadgesModal({
  badges,
  onClose,
}: {
  badges: Array<Badge & { unlocked: boolean }>;
  onClose: () => void;
}) {
  const { lang } = useLanguage();
  const unlockedCount = badges.filter(b => b.unlocked).length;
  
  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="neo-card-static bg-white dark:bg-[#1e2a3a] p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-black text-black dark:text-white">
            🎖️ {lang === 'ko' ? '배지 컬렉션' : 'Badge Collection'}
          </h3>
          <button
            onClick={onClose}
            className="text-2xl text-terminal-muted hover:text-terminal-text"
          >
            ×
          </button>
        </div>

        <p className="text-sm text-terminal-muted mb-3">
          {lang === 'ko' 
            ? `${unlockedCount}/${badges.length}개 획득`
            : `${unlockedCount}/${badges.length} earned`
          }
        </p>

        {/* 배지 목록 - 3.5개 보이는 높이로 스크롤 */}
        <div className="space-y-3 max-h-[280px] overflow-y-auto pr-1 scrollbar-thin">
          {badges.map((badge) => (
            <div
              key={badge.id}
              className={`p-3 rounded-lg border-2 transition-all ${
                badge.unlocked 
                  ? 'border-[var(--neo-mint)] bg-[var(--neo-mint)]/10' 
                  : 'border-black/10 dark:border-white/10 bg-black/5 dark:bg-white/5 opacity-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className={`text-2xl ${badge.unlocked ? '' : 'grayscale'}`}>
                  {badge.emoji}
                </span>
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-sm text-black dark:text-white">
                    {lang === 'ko' ? badge.nameKo : badge.nameEn}
                    {badge.unlocked && <span className="ml-2 text-status-go">✓</span>}
                  </h4>
                  <p className="text-xs text-terminal-muted">
                    {lang === 'ko' ? badge.descriptionKo : badge.descriptionEn}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={onClose}
          className="neo-btn w-full bg-black text-white dark:bg-[var(--neo-mint)] dark:text-black mt-6"
        >
          {lang === 'ko' ? '닫기' : 'Close'}
        </button>
      </div>
    </div>
  );
}

// 비로그인 랜딩 페이지 컴포넌트
function GuestLanding({
  onTryDemo,
  onSignUp,
}: {
  onTryDemo: () => void;
  onSignUp: () => void;
}) {
  const { t, lang } = useLanguage();
  const [currentNews, setCurrentNews] = useState<SoftwareNewsItem | null>(null);

  useEffect(() => {
    getCurrentNews(lang).then(setCurrentNews).catch(console.error);
  }, [lang]);

  // CTA 배너 문구 (랜덤 선택)
  const trustedByMessage = useMemo(() => {
    const options = t.home?.trustedByOptions || ['QA 팀을 위해 만들었어요'];
    return options[Math.floor(Math.random() * options.length)];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]); // lang 변경 시에만 재계산

  return (
    <div className="space-y-6 md:space-y-8" data-testid="guest-landing">
      {/* 히어로 섹션 */}
      <section className="neo-card-static bg-gradient-to-br from-[var(--neo-lime)] via-[var(--neo-mint)] to-[var(--neo-blue)] dark:from-[#1e2a3a] dark:via-[#162638] dark:to-[#0f1a2a] p-6 md:p-10 text-center" data-testid="landing-hero">
        <h1 
          className="text-2xl md:text-4xl lg:text-5xl font-black text-black dark:text-white mb-4 whitespace-pre-line leading-tight"
          data-testid="landing-hero-title"
        >
          {t.home?.heroTitle || 'AI가 판단하는\n릴리즈 Go/No-Go'}
        </h1>
        <p className="text-sm md:text-base text-black/70 dark:text-white/70 mb-6 md:mb-8 whitespace-pre-line">
          {t.home?.heroSubtitle || '기획 의도, 리스크, QA 결과를 입력하면\nAI가 릴리즈 가능 여부를 판단해드려요'}
        </p>
        
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => {
              analytics.demoSignupClick();
              onTryDemo();
            }}
            className="neo-btn bg-white text-black px-6 py-3 text-sm md:text-base"
            data-testid="landing-try-demo-btn"
          >
            🎯 {t.home?.tryDemo || '데모 체험하기'}
          </button>
          <button
            onClick={onSignUp}
            className="neo-btn bg-black text-[var(--neo-lime)] dark:bg-[var(--neo-mint)] dark:text-black px-6 py-3 text-sm md:text-base"
            data-testid="landing-signup-btn"
          >
            🚀 {t.home?.signUpFree || '무료로 시작하기'}
          </button>
        </div>
      </section>

      {/* 기능 소개 - 3열 그리드 (모바일: 1열) */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="landing-features">
        <FeatureCard
          icon="1️⃣"
          title={t.home?.feature1Title || '3단계 검증'}
          description={t.home?.feature1Desc || '기획 → 리스크 → 테스트 순차 검증'}
          color="bg-[var(--neo-lime)]"
          testId="feature-1"
        />
        <FeatureCard
          icon="2️⃣"
          title={t.home?.feature2Title || 'AI 분석'}
          description={t.home?.feature2Desc || '맥락을 이해하는 정밀한 평가'}
          color="bg-[var(--neo-mint)]"
          testId="feature-2"
        />
        <FeatureCard
          icon="3️⃣"
          title={t.home?.feature3Title || '팀 협업'}
          description={t.home?.feature3Desc || '팀 전체 릴리즈 품질 대시보드'}
          color="bg-[var(--neo-yellow)]"
          testId="feature-3"
        />
      </section>

      {/* 소프트웨어 뉴스 (비로그인에서도 표시) */}
      {currentNews && (
        <section className="neo-card-static bg-gradient-to-br from-[var(--neo-blue)] to-[var(--neo-mint)] dark:from-[#0f1a2a] dark:to-[#1e2a3a] p-4 md:p-6" data-testid="landing-news">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">📰</span>
            <h3 className="text-sm font-black text-black dark:text-white uppercase">
              {lang === 'ko' ? '소프트웨어 뉴스' : 'Software News'}
            </h3>
          </div>
          <h4 className="text-base font-bold text-black dark:text-white leading-snug mb-2">
            {currentNews.title}
          </h4>
          <p className="text-sm text-black/70 dark:text-white/70 line-clamp-2">
            {currentNews.summary}
          </p>
        </section>
      )}

      {/* CTA 배너 */}
      <section className="neo-card-static bg-[var(--neo-pink)] dark:bg-[#2d3a4f] p-6 text-center" data-testid="landing-cta">
        <p className="text-black dark:text-white font-bold mb-4">
          {trustedByMessage}
        </p>
        <div className="flex justify-center gap-4 text-2xl opacity-50">
          <span>🏢</span>
          <span>🏭</span>
          <span>🏛️</span>
          <span>🏗️</span>
        </div>
      </section>
    </div>
  );
}

// 기능 카드 컴포넌트
function FeatureCard({
  icon,
  title,
  description,
  color,
  testId,
}: {
  icon: string;
  title: string;
  description: string;
  color: string;
  testId: string;
}) {
  return (
    <div className={`neo-card-static ${color} dark:bg-[#1e2a3a] p-5`} data-testid={testId}>
      <div className="text-3xl mb-3">{icon}</div>
      <h3 className="text-sm font-black text-black dark:text-white uppercase mb-2">{title}</h3>
      <p className="text-xs text-black/70 dark:text-white/70">{description}</p>
    </div>
  );
}
